import { Id } from "@/jenesys-types/data/common";

export type Client = Company;

export interface CreateClientPayload {
  organisation_name: string;
  chat_platform: string;
  jackpod_client: boolean;
  is_vat_registered: boolean;
  reg_country: string;
}

export interface Company {
  id: string;
  name: string;
  admin_email: string;
  operating_addresses: Array<string>;
  reg_address: any;
  reg_country: string;
  reg_number: string;
  reg_type: string;
  reg_type_normalised: string;
  tins: Array<string>;
  is_data_validation_enabled: boolean;
  is_line_items_extraction_enabled: boolean;
  industry: string | null;
  accounting_platform: string;
  billing_address: {
    street?: string;
    city?: string;
    postcode?: string;
  };
  email_address: string | null;
  forwarding_email: string;
  hq_address: {
    street?: string;
    city?: string;
    postcode?: string;
  };
  phone_number: string | null;
  primary_user_name: string | null;
  trading_name: string | null;
  uk_company_number: string | null;
  vat_number: string | null;
  home_currency: string | null;
  is_vat_registered: boolean;
  type: CompanyTypes;
  role?: string;
  can_publish?: boolean;
  jackpod_client?: boolean;
}

export interface CompanySummary {
  company_id: Company["id"];
  company_name: string;
  total_time: number;
  published: number;
  completed: number;
  processed: number;
  to_validate: number;
  billable_amount: string;
  failed: number;
}

export interface ExtractionSummary {
  billable_amount: string;
  units: number;
  extraction_duration: number;
  total_published: number;
  total_completed: number;
  total_failed: number;
  total_processed: number;
  total_to_validate: number;
  detailed_company_summary: Array<CompanySummary>;
}

export interface ClientTransactions {
  total: number;
  limit: number;
  offset: number;
  items: ClientTransaction[];
}
export interface ClientTransaction {
  document_id: string;
  status: string;
  extracted_at: string;
  extraction_duration: number;
  unit: number;
  billable_amount: number;
  data: any;
  is_zero_time?: boolean;
  zero_time_reason?: string | null;
}

export const statusStyles = {
  PENDING: {
    text: "Extracting",
    color: "gray.600",
    bg: "blackAlpha.100",
    index: 5
  },
  IN_PROGRESS: {
    text: "In Progress",
    color: "blue.600",
    bg: "blue.100",
    index: 7
  },
  ON_HOLD: { text: "On Hold", color: "orange.600", bg: "orange.100", index: 6 },
  FAILED: {
    text: "Failed",
    color: "red.600",
    bg: "red.100",
    index: 4
  },
  REVIEWED: {
    text: "Reviewing",
    color: "orange.500",
    bg: "#FFDCC2",
    index: 2
  },
  SYNCED: { text: "Synced", color: "green.600", bg: "green.100", index: 11 },
  COMPLETED: {
    text: "Ready",
    color: "blue.600",
    bg: "blue.100",
    index: 1
  },
  PUBLISHING: {
    text: "Publishing",
    color: "gray.600",
    bg: "blackAlpha.100",
    index: 2.5
  },
  PUBLISHED: {
    text: "Published",
    color: "green.600",
    bg: "green.100",
    index: 3
  },
  EDITED: {
    text: "Edited",
    color: "orange.600",
    bg: "orange.100",
    index: 8
  },
  REPUBLISHED: {
    text: "Published",
    color: "green.600",
    bg: "green.100",
    index: 9
  },
  DELETED: {
    text: "Deleted",
    color: "red.600",
    bg: "red.100",
    index: 10
  },
  DUPLICATE: {
    text: "Duplicate",
    color: "#9A8D00",
    bg: "#FFEA0036",
    index: 12
  },
  DISCARDED: {
    text: "Discarded",
    color: "red.300",
    bg: "red.100",
    index: 13
  },
  "": { text: "All", color: "", bg: "", index: 12 }
};

export const transactionSources: {
  [key: string]: { label: string; icon: string; displayLabel: string };
} = {
  WEB_APP: {
    label: "DASHBOARD",
    icon: "/icon.png",
    displayLabel: "Jack Upload"
  },
  GMAIL_FETCH: {
    label: "GMAIL",
    icon: "/images/gmail.svg",
    displayLabel: "Gmail Fetch"
  },
  OUTLOOK_FETCH: {
    label: "OUTLOOK",
    icon: "/images/microsoft.svg",
    displayLabel: "Outlook Fetch"
  },
  EMAIL_FORWARDING: {
    label: "EMAIL FORWARDING",
    icon: "/images/email-forward.svg",
    displayLabel: "Email Forwarding"
  },
  WHATSAPP: {
    label: "WHATSAPP",
    icon: "/images/whatsapp.svg",
    displayLabel: "WhatsApp"
  }
};

export interface TransactionComment {
  id: string;
  comment: string;
  created_at: TimeStamp;
  updated_at: TimeStamp;
  author: Omit<User, "companies">;
  mentioned_users?: Array<{
    user_id: string;
    text: string;
  }>;
}

export interface TransactionHistory {
  id: string;
  created_at: TimeStamp;
  details: string;
  document_id: string;
  event:
    | "UPLOADED"
    | "EXTRACTED"
    | "EDITED"
    | "PUBLISHED"
    | "REPUBLISHED"
    | "FAILED_TO_PUBLISH"
    | "FAILED_TO_REPUBLISH";
  additional_info: {
    document_data: string | null;
    document_source: string | null;
    document_status: Transaction["status"];
    error_details: any;
  };
  source: "USER" | "JACK";
  updated_at: TimeStamp;
}

export type TimeStamp = string | number | null;
export interface Transaction {
  company_id: Id;
  deleting_reason: string | null;
  image_url: string;
  image_download_url: string;
  original_document_id: string | null;
  user_id: Id;
  finished_at: TimeStamp;
  id: Id;
  inference_time: TimeStamp;
  started_at: TimeStamp;
  created_at: TimeStamp;
  status: keyof typeof statusStyles;
  source: string;
  filename: string;
  extracted_data: TransactionData;
  matched_transaction: { id: Id; account_id: Id } | null;
  possibly_matched_transaction: { id: Id; account_id: Id } | null;
  date_of_invoice: TimeStamp;
  end_time: TimeStamp;
  invoice_id: Id;
  start_time: TimeStamp;
  supplier: string;
  total: string;
  publishing_failure_reason: string | null;
}

export interface TransactionData {
  document_type: string;
  vat: number;
  total: number;
  tax_code: string;
  account_code: string;
  vat_exclusive: boolean;
  currency: string;
  supplier_name: string;
  supplier_id: string;
  invoice_id: string;
  vat_number: string;
  line_items: Array<TransactionDataLineItem>;
  date_of_invoice: string;
  date_payment_due: string;
  payment_status: string;
  payment_source: string;
  addresses: Array<{
    type: string;
    first_line: string;
    second_line: string;
    city: string;
    country: string;
    postcode: string;
  }>;
  memo: string;
  is_duplicate: boolean;
  bank_details: Array<{
    type: string;
    account_number: string;
    account_name: string;
    sort_code: string;
    iban: string;
    swift_code: string;
    bank_name: string;
    street_address: string;
    city: string;
    country: string;
    post_code: string;
    full_name: string;
    email_address: string;
    phone_number: string;
    reference: string;
    note: string;
  }>;
  original_line_items: Array<TransactionDataLineItem>;
  original_extracted_total: number;
  original_extracted_vat: number;
  // Missing fields from current structure - converted to snake_case
  end_time: TimeStamp;
  inference_time: number;
  start_time: TimeStamp;
  supplier_description: string | null;
  expense_policy_review: string | null;
  expense_policy_review_status: string | null;
  doc_transcript: string | null;
  discount_total: number;
  service_charge: number;
  delivery_charge: number;
}

export interface TransactionDataLineItem {
  vat: string;
  total: string;
  tax_code: string;
  account_code?: string;
  quantity: string;
  unit_price: string;
  description?: string;
  tracking_categories: Array<{
    category_id: string | null;
    option_id: string | null;
  }>;
  // Missing fields from current structure - converted to snake_case
  discount?: number;
  id?: string;
}

export enum UserRoles {
  SUPER_ADMIN = "SUPER_ADMIN",
  PRACTICE_ADMIN = "PRACTICE_ADMIN",
  PRACTICE_FINANCE = "PRACTICE_FINANCE",
  COMPANY_ADMIN = "COMPANY_ADMIN",
  COMPANY_USER = "COMPANY_USER",
  DAO_ADMIN = "DAO_ADMIN",
  DAO_FINANCE = "DAO_FINANCE",
  DAO_USER = "DAO_USER"
}

export enum CompanyTypes {
  COMPANY = "company",
  PRACTICE = "practice",
  DAO = "dao"
}

export interface User {
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  propel_auth_id: string;
  companies: Array<Company>;
  seen_intro: boolean;
  agreed_tc: string | null;
}

export interface Notification {
  id: string;
  user_id: string;
  date: string;
  status: "SUCCESS" | "FAILURE" | "INFO";
  title: string;
  is_read: boolean;
  document_publishing?: {
    company_id: string;
    document_id: string;
  } | null;
  communication_hub?: {
    company_id: string;
    query_id: string | null;
    commenter_name: string | null;
    type:
      | "QUERY_COMMENT"
      | "QUERY"
      | "CHASER"
      | "REMINDER"
      | "INVOICE_UPLOAD"
      | "QUERY_COMMENT_TAGGED";
  } | null;
}

export interface RefactoredCompany {
  id: Id;
  name: string;
  role: UserRoles;
  type: CompanyTypes;
}

export interface DateRange {
  label: string;
  after: Date | null | string;
  before: Date | null | string;
}

export interface Connection {
  company_id: Company["id"];
  id: string;
  invited_by_id: User["id"];
  platform: string;
  status: "pending" | "connected" | string;
  user_id: User["id"];
}

export interface WhatsappConnectionStatus {
  id?: string;
  number?: string;
  status?: "invited" | "connected";
}

export interface TeamMember {
  connections: Connection[];
  whatsapp_connection: WhatsappConnectionStatus | null;
  role: string | null;
  position: string;
  department_id?: IDepartment["id"];
  department: IDepartment | null;
  id: string;
  user: {
    email: string;
    first_name: string;
    last_name: string;
    seen_intro: boolean;
    agreed_tc: string | null;
    id: string;
    is_email_confirmed: boolean;
  };
}

export interface IDepartment {
  id: string;
  name: string;
}

export interface IIntegrationsStatus {
  status?: string;
  name?: string;
  tenant_name?: string | null;
  user_status?: string;
  subscription_plan?: string;
  company_status?: string;
  last_refreshed?: string | null;
  initially_connected_user?: User | null;
  is_vat_registered?: boolean | null;
}

export interface IAccountingRecord {
  id: string;
  name: string;
  platform_record_id?: string;
  integration: string;
}

export interface IXeroTrackingCategoryRaw {
  Name: string;
  Options: {
    TrackingOptionID: string;
    Name: string;
    Status: string;
    HasValidationErrors: boolean;
    IsActive: boolean;
    IsArchived: boolean;
    IsDeleted: boolean;
  }[];
  Status: string;
  TrackingCategoryID: string;
}

export interface IQuickBooksTrackingCategoryRaw {
  Name: string;
  SubClass: boolean;
  ParentRef?: { value: string };
  FullyQualifiedName: string;
  Active: boolean;
  domain: string;
  sparse: boolean;
  Id: string;
  SyncToken: string;
  MetaData: {
    CreateTime: string;
    LastUpdatedTime: string;
  };
}

export interface ISageTrackingCategoryRaw {
  id: string;
  displayed_as: string;
  $path: string;
  created_at: string;
  updated_at: string;
  active_areas: any[];
  analysis_type_level: {
    id: number;
    name: string;
    identifier: string;
  };
  analysis_type_categories: {
    id: string;
    displayed_as: string;
    $path: string;
    created_at: string;
    updated_at: string;
    code: string;
    name: string;
    combined_id: string;
    analysis_type: {
      id: string;
      displayed_as: string;
      $path: string;
    };
  }[];
  name: string;
}

export type ITrackingCategory =
  | (IAccountingRecord & {
      integration: "XERO";
      company_id: string;
      raw: IXeroTrackingCategoryRaw;
    })
  | (IAccountingRecord & {
      integration: "QUICKBOOKS";
      company_id: string;
      raw: IQuickBooksTrackingCategoryRaw;
    })
  | (IAccountingRecord & {
      integration: "SAGE";
      company_id: string;
      raw: ISageTrackingCategoryRaw;
    });

export interface IAccountingData {
  accounts: (IAccountingRecord & {
    code: string;
  })[];
  tax_codes: (IAccountingRecord & {
    effective_rate?: number;
  })[];
  businesses: IAccountingRecord[];
  tracking_categories: ITrackingCategory[];
  vendors: IAccountingRecord[];
  payment_sources: IAccountingRecord[];
}

type AccountingDataSynchronizationSteps = {
  store_accounts: string;
  store_tax_codes: string;
  store_vendors: string;
  store_invoices: string;
  store_currencies: string;
  store_tracking_categories: string;
};

export interface IAccountingDataStatus {
  status: "in_progress" | "pending" | "failed" | "completed";
  last_step: keyof AccountingDataSynchronizationSteps;
  completed_steps: Array<keyof AccountingDataSynchronizationSteps>;
  timestamp: string;
  next_call_offset: number;
}

export interface IChartOfAccounts extends IAccountingRecord {
  integration: string;
  company_id: Company["id"];
  raw: string | null;
  is_deleted: false;
  description: string | null;
  code: string | number | null;
  tax_code: IAccountingData["tax_codes"][0] | null;
  currency: string | null;
  type: string;
  updated_at: string | null;
}

export interface ICompanyAccountingIntegrations {
  user: User;
  platform: string;
}

export interface IBank {
  id: string;
  institution_id: string;
  name: string;
  raw: {
    id: string;
    name: string;
    media: [
      {
        type: "icon";
        source: string;
      },
      {
        type: "logo";
        source: string;
      }
    ];
    features: Array<string>;
    fullName: string;
    countries: Array<{
      displayName: string;
      countryCode2: string;
    }>;
    credentialsType: string;
    environmentType: string;
  };
}

export interface BankAccount {
  id: string;
  account_id: string | null;
  account_number: string | null;
  bank_connection_id: string;
  bank_account_id: string;
  name: string;
  type: string;
  currency: string;
  usage_type: string;
  account_type: string;
  status: "ACTIVE" | "EXPIRED" | string;
}

export interface IBankConnection {
  institution_id: IBank["institution_id"];
  company_id: Company["id"];
  connected_by_id: User["id"];
  status: "ACTIVE" | "EXPIRED" | string;
  institution: Pick<IBank, "name" | "raw" | "id">;
  last_confirmed_at: string;
  reconfirm_by: string;
  created_at: string;
  id: string;
  accounts: Array<BankAccount>;
}

export interface BankReconciliation {
  bank: IBank["name"];
  icon: string;
  accounts: Array<{
    balance: number;
    id: string;
    name: string;
    unreconciled: number;
  }>;
}

export interface BankStatement {
  account: BankAccount;
  amount: number;
  booking_date: string;
  currency: string;
  date: string;
  description: string;
  id: string;
  reference: string;
  status: "BOOKED" | "PENDING";
  reconciliation_status: "RECONCILED" | "UNRECONCILED" | "POSSIBLY_MATCHED";
  supplier: string;
  transaction_id: string;
  value_date: string;
  possibly_matched_document?: Transaction;
  matched_document?: Transaction;
}

export interface BankStatementUpload {
  id: string;
  status: "UPLOADED" | "EXTRACTING" | "EXTRACTED" | "FAILED" | "CONFIRMED";
  user_id: string;
  company_id: string;
  pdf_url: string;
  pdf_download_url: string;
  bank_account_id: string;
  filename: string;
  created_at: string;
  updated_at: string;
  total_amount: 0;
  extracted_details: Array<{
    Supplier: string;
    Date: string;
    Amount: string | number;
    Description: string;
  }>;
}

export interface IBankStatementDocument {
  filename: string;
  download_pdf_url: string;
  download_csv_url: string;
  created_at: string;
  updated_at: string;
  status: "UPLOADED" | "CONVERTED" | "PROCESSING" | "FAILED";
  user: User;
  user_id: string;
  id: string;
  company_id: string;
}

export interface EmailForwardingSender {
  id: string;
  email: string;
  full_name?: string | null;
  position?: string | null;
  status: "PENDING" | "ALLOWED" | "BLOCKED" | "REJECTED";
  created_at?: string;
  reason?: string | null;
}

export interface BankStatementSummary {
  bank_name: IBank["name"];
  account_name: BankAccount["name"];
  statement_total: number;
  jack_total: number;
  difference: number;
}

export interface DuplicateInfo {
  invoice_id: string;
  supplier: string;
  date_of_invoice: TimeStamp;
  total: string;
  document_url: Transaction["image_url"];
  document_id: string;
}

export interface DuplicateCheckData {
  current_document_extraction_data: DuplicateInfo;
  original_document_extraction_data: DuplicateInfo;
}
